import { forwardRef } from 'react';
import { cn } from './cn.js';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required — the button has no text, so it must carry its own accessible name. */
  label: string;
  variant?: 'ghost' | 'outline';
  size?: 'sm' | 'md';
}

/**
 * The circular icon button the reference uses for card overflow menus and top-bar actions.
 *
 * `label` is required rather than optional: an icon-only button with no accessible name is
 * invisible to a screen reader, and making it optional guarantees someone forgets.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', size = 'md', className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      // Opts this control into the 44px touch target in tokens.css. An icon button is the
      // smallest thing in the product and the one a thumb misses most.
      data-touch-target=""
      className={cn(
        // `relative` anchors that target's pseudo-element. It is a base class rather than a CSS
        // rule so `cn`'s tailwind-merge lets a caller positioning the button absolutely — the
        // hero image's edit button does — still win; the pseudo-element anchors either way.
        'relative inline-flex shrink-0 items-center justify-center rounded-pill text-ink-muted transition-colors duration-[var(--duration-fast)]',
        size === 'sm' ? 'size-7' : 'size-9',
        variant === 'ghost' && 'hover:bg-surface-sunk hover:text-ink',
        variant === 'outline' && 'bg-surface ring-1 ring-rule hover:bg-surface-sunk hover:text-ink',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/** The "..." glyph, sized to sit inside IconButton. */
export function MoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="3" cy="8" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="13" cy="8" r="1.4" />
    </svg>
  );
}
